import React, { useEffect, useState } from 'react';
import API from './api';
import QRCode from 'react-qr-code';
import StudentDetailsModal from './StudentDetailsModal';
import { FaBookOpen } from 'react-icons/fa';
import { MapModal } from './MapModal';
import { MapPin } from 'lucide-react';
import { SiGooglemaps } from 'react-icons/si';

// --- CANCEL MODAL ---
function CancelModal({ open, onClose, onSubmit, loading }) {
  const [reason, setReason] = useState('');
  const REASONS = [
    'Cancelled by teacher',
    'Cancelled by family',
    'Late cancellation',
    'NoShow/withAppointmentConfirmation',
  ];

  useEffect(() => {
    if (!open) setReason('');
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded shadow-lg w-full max-w-md">
        <h3 className="text-lg font-bold mb-4">Cancel Visit</h3>
        <p className="mb-2">Are you sure you want to cancel this visit?</p>
        <label className="block mb-1 font-semibold">Reason for cancelation:</label>
        <select
          className="border rounded px-3 py-2 w-full mb-4"
          value={reason}
          onChange={e => setReason(e.target.value)}
        >
          <option value="">-- Choose reason --</option>
          {REASONS.map(r => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <div className="flex justify-end gap-2 mt-2">
          <button onClick={onClose} className="px-4 py-2 bg-gray-300 rounded">
            Back
          </button>
          <button
            onClick={() => onSubmit(reason)}
            disabled={!reason || loading}
            className={`px-4 py-2 bg-purple-700 text-white rounded ${
              !reason || loading ? 'opacity-60 pointer-events-none' : ''
            }`}
          >
            {loading ? 'Canceling...' : 'Cancel Visit'}
          </button>
        </div>
      </div>
    </div>
  );
}

function addMinutesHHMM(hhmm, mins) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  const d = new Date(2000, 0, 1, h || 0, m || 0, 0);
  d.setMinutes(d.getMinutes() + (mins || 0));
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
}

function VisitTimeType({ scheduled_time, type, event_end_time }) {
  const from = scheduled_time || '';
  const to =
    (event_end_time && event_end_time.slice(0, 5)) || addMinutesHHMM(from, 45);

  const badgeColor =
    type === 'event'
      ? 'bg-yellow-100 text-orange-800'
      : 'bg-green-100 text-green-800';
  const label = type === 'event' ? 'Event' : 'Standard';

  return (
    <div className="flex flex-col items-start">
      <span className="font-mono text-base">
        {from && to ? `${from}–${to}` : '-'}
      </span>
      <span className={`mt-1 px-2 py-0.5 rounded text-xs ${badgeColor}`}>
        {label}
      </span>
    </div>
  );
}

function StatusBadge({ status, children }) {
  let color = 'bg-gray-200 text-gray-700';
  if (status === 'pending') color = 'bg-yellow-100 text-yellow-800';
  if (status === 'accepted') color = 'bg-green-100 text-green-700';
  if (status === 'rejected') color = 'bg-red-100 text-red-700';
  if (status === 'draft') color = 'bg-blue-100 text-blue-700';
  if (status === 'complete') color = 'bg-green-200 text-green-900';
  if (status === 'canceled') color = 'bg-purple-200 text-purple-700';
  return (
    <span className={`inline-block px-2 py-1 rounded text-xs font-semibold ${color}`}>
      {children || status}
    </span>
  );
}

function ProgramBadge({ program_type }) {
  if (!program_type) return null;
  if (program_type === 'PI') {
    return (
      <span className="ml-1 px-2 py-0.5 rounded-full border border-orange-500 text-orange-600 text-[10px]">
        PI
      </span>
    );
  }
  if (program_type === 'EI') {
    return (
      <span className="ml-1 px-2 py-0.5 rounded-full border border-blue-500 text-blue-600 text-[10px]">
        EI
      </span>
    );
  }
  return null;
}

function groupByDate(visits) {
  return visits.reduce((acc, v) => {
    const date = v.scheduled_at.slice(0, 10);
    if (!acc[date]) acc[date] = [];
    acc[date].push(v);
    return acc;
  }, {});
}

const DOMAINS = [
  { key: 'notes_cognitive', label: 'Cognitive' },
  { key: 'notes_language', label: 'Language' },
  { key: 'notes_gross_motor', label: 'Gross Motor' },
  { key: 'notes_fine_motor', label: 'Fine Motor' },
  { key: 'notes_social', label: 'Social' },
  { key: 'notes_self_help', label: 'Self Help' },
];

function getVisitRowProps(v, serverDateStr) {
  const reassigned = v.teacher_id !== v.original_teacher_id;
  const status = v.reassigned_status || '';
  const visitDateStr = v.scheduled_at.slice(0, 10);

  let rowClass = 'hover:bg-gray-50';
  let hideActions = false;

  const isCanceled = v.status === 'canceled';

  if (isCanceled) {
    rowClass += ' bg-purple-100 opacity-80';
    hideActions = true;
  } else {
    if (visitDateStr < serverDateStr && v.status === 'draft') {
      hideActions = true;
      rowClass += ' opacity-60 bg-gray-100';
    } else if (visitDateStr > serverDateStr) {
      hideActions = true;
      rowClass += ' opacity-80 bg-yellow-50';
    } else if (visitDateStr === serverDateStr) {
      rowClass += ' bg-green-50';
    }

    if (reassigned && (status === 'pending' || status === 'rejected')) {
      hideActions = true;
      rowClass += status === 'rejected' ? ' bg-red-50' : ' bg-orange-50';
    }
  }

  return { rowClass, hideActions, reassigned, status, visitDateStr, isCanceled };
}

function compareDate(a, b) {
  return a.localeCompare(b);
}

function MyVisits() {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [location, setLocation] = useState({ lat: null, lon: null });
  const [filters, setFilters] = useState({ dateFrom: '', dateTo: '' });

  const [qrModalVisitId, setQrModalVisitId] = useState(null);
  const [visitConfirmed, setVisitConfirmed] = useState(false);

  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesModalVisit, setNotesModalVisit] = useState(null);
  const [notesFields, setNotesFields] = useState({});
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesEditMode, setNotesEditMode] = useState(false); // false = finalize (checkout), true = edit-only

  const [studentDetailsModal, setStudentDetailsModal] = useState({ open: false, student: null });
  const [serverDateStr, setServerDateStr] = useState(null);

  const [cancelVisitId, setCancelVisitId] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [studentFilter, setStudentFilter] = useState('');
  const [studentOptions, setStudentOptions] = useState([]);

  const [showNotesHistory, setShowNotesHistory] = useState(false);
  const [notesHistory, setNotesHistory] = useState([]);
  const [notesHistoryLoading, setNotesHistoryLoading] = useState(false);
  const [notesHistoryStudent, setNotesHistoryStudent] = useState('');

  const [mapModal, setMapModal] = useState({ open: false, student: null, teacher: null });

  // INIT: server time + geo
  useEffect(() => {
    API.get('/server-time')
      .then(res => setServerDateStr(res.data.serverTime.slice(0, 10)))
      .catch(() => setServerDateStr(null));

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => setLocation({ lat: pos.coords.latitude, lon: pos.coords.longitude }),
        err => console.warn(err),
        { enableHighAccuracy: true }
      );
    }
  }, []);

  // default filter: last 15 days
  useEffect(() => {
    if (filters.dateFrom || filters.dateTo) return;
    const base = serverDateStr ? new Date(serverDateStr) : new Date();
    if (Number.isNaN(base.getTime())) return;
    const end = new Date(base);
    const start = new Date(base);
    start.setDate(start.getDate() - 15);
    const pad = n => String(n).padStart(2, '0');
    const fmt = d =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    setFilters({ dateFrom: fmt(start), dateTo: fmt(end) });
  }, [serverDateStr]); // jednom kad znamo serverDateStr

  const getCurrentWeekRange = baseDateStr => {
    const base = new Date(baseDateStr);
    const day = base.getDay() === 0 ? 7 : base.getDay();
    const monday = new Date(base);
    monday.setDate(base.getDate() - day + 1);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const pad = n => String(n).padStart(2, '0');
    const fmt = d =>
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    return { monday: fmt(monday), sunday: fmt(sunday) };
  };

  const fetchVisits = async (custom) => {
    setLoading(true);
    try {
      const f = custom || filters;
      const params = {};
      if (f.dateFrom) params.date_from = f.dateFrom;
      if (f.dateTo) params.date_to = f.dateTo;
      const res = await API.get('/visits', { params });
      const data = res.data || [];
      data.sort((a, b) => b.scheduled_at.localeCompare(a.scheduled_at));
      setVisits(data);
    } catch (err) {
      console.error('Failed to load visits', err);
    } finally {
      setLoading(false);
    }
  };

  // refetch kada se promene filter datumi
  useEffect(() => {
    if (!filters.dateFrom && !filters.dateTo) return;
    fetchVisits();
    // eslint-disable-next-line
  }, [filters.dateFrom, filters.dateTo]);

  // student filter options
  useEffect(() => {
    const unique = {};
    visits.forEach(v => {
      if (!unique[v.student_id]) {
        unique[v.student_id] = {
          id: v.student_id,
          first_name: v.student_first_name,
          last_name: v.student_last_name,
        };
      }
    });
    const opts = Object.values(unique).sort((a, b) =>
      `${a.last_name || ''}${a.first_name || ''}`.localeCompare(
        `${b.last_name || ''}${b.first_name || ''}`
      )
    );
    setStudentOptions(opts);
    if (studentFilter && !unique[studentFilter]) setStudentFilter('');
  }, [visits, studentFilter]);

  const setQuickFilter = (type) => {
    if (!serverDateStr) return;
    let nf = { ...filters };
    if (type === 'today') {
      nf = { dateFrom: serverDateStr, dateTo: serverDateStr };
    } else if (type === 'week') {
      const { monday, sunday } = getCurrentWeekRange(serverDateStr);
      nf = { dateFrom: monday, dateTo: sunday };
    } else if (type === 'future') {
      nf = { dateFrom: serverDateStr, dateTo: '' };
    }
    setFilters(nf);
    fetchVisits(nf);
  };

  const openMapModal = async (studentId, teacherId) => {
    try {
      const res = await API.get('/visit-map-locations', {
        params: { student_id: studentId, teacher_id: teacherId },
      });
      setMapModal({
        open: true,
        student: res.data.student,
        teacher: res.data.teacher,
      });
    } catch {
      alert('Failed to fetch map data');
    }
  };

  const openNotesHistoryModal = async (studentId, studentName) => {
    setNotesHistoryLoading(true);
    setShowNotesHistory(true);
    setNotesHistoryStudent(studentName);
    try {
      const res = await API.get(`/students/${studentId}/notes-history`);
      setNotesHistory(res.data || []);
    } catch {
      setNotesHistory([]);
    } finally {
      setNotesHistoryLoading(false);
    }
  };

  const openStudentModal = async (studentId) => {
    try {
      const res = await API.get(`/students/${studentId}/full`);
      setStudentDetailsModal({ open: true, student: res.data });
    } catch {
      alert('Failed to load student details');
    }
  };

  const handleCheckin = async (id) => {
    if (!location.lat) return alert('Geolocation required');
    try {
      await API.patch(`/visits/${id}/checkin`, location);
      fetchVisits();
    } catch {
      alert('Failed to check-in');
    }
  };

  const openNotesModal = (visit, mode) => {
    setNotesModalVisit(visit);
    setNotesFields({
      notes_cognitive: visit.notes_cognitive || '',
      notes_language: visit.notes_language || '',
      notes_gross_motor: visit.notes_gross_motor || '',
      notes_fine_motor: visit.notes_fine_motor || '',
      notes_social: visit.notes_social || '',
      notes_self_help: visit.notes_self_help || '',
    });
    setNotesEditMode(mode === 'edit'); // 'edit' => samo notes; 'finalize' => checkout+complete
    setShowNotesModal(true);
  };

  const handleCheckout = (visit) => {
    openNotesModal(visit, 'finalize');
  };

  const handleSaveNotes = async () => {
    if (!notesModalVisit) return;
    setNotesSaving(true);
    try {
      if (!notesEditMode) {
        // FINALIZE: checkout + status=complete
        if (!location.lat) {
          alert('Geolocation required!');
          setNotesSaving(false);
          return;
        }
        await API.patch(`/visits/${notesModalVisit.id}/checkout`, location);
        await API.put(`/visits/${notesModalVisit.id}`, {
          ...notesFields,
          status: 'complete',
        });
      } else {
        // EDIT: samo notes
        await API.put(`/visits/${notesModalVisit.id}`, {
          ...notesFields,
        });
      }
      setShowNotesModal(false);
      setNotesModalVisit(null);
      fetchVisits();
    } catch (err) {
      console.error(err);
      alert('Failed to save notes!');
    } finally {
      setNotesSaving(false);
    }
  };

  const handleCancelVisit = async (reason) => {
    if (!reason || !cancelVisitId) return;
    setCancelLoading(true);
    try {
      await API.patch(`/visits/${cancelVisitId}/cancel`, { reason });
      setCancelVisitId(null);
      fetchVisits();
    } catch {
      alert('Failed to cancel visit!');
    } finally {
      setCancelLoading(false);
    }
  };

  // QR polling
  useEffect(() => {
    let interval;
    if (qrModalVisitId && !visitConfirmed) {
      interval = setInterval(async () => {
        try {
          const res = await API.get(`/visits/${qrModalVisitId}`);
          if (res.data && res.data.g_sign_confirmed) {
            setVisitConfirmed(true);
            fetchVisits();
            clearInterval(interval);
          }
        } catch {
          // ignore
        }
      }, 2000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
    // eslint-disable-next-line
  }, [qrModalVisitId, visitConfirmed]);

  const handleCloseQRModal = () => {
    setQrModalVisitId(null);
    setVisitConfirmed(false);
    fetchVisits();
  };

  // *** CHANGED: uklonjen uslov po reassigned_status,
  //              dozvoljen cancel za danas + budućnost, status=draft, bez checkin-a.
  const canCancel = (v, visitDateStr) =>
    compareDate(visitDateStr, serverDateStr || '') >= 0 &&
    v.status === 'draft' &&
    !v.checkin_time;

  const filteredVisits = studentFilter
    ? visits.filter(v => String(v.student_id) === String(studentFilter))
    : visits;

  const grouped = groupByDate(filteredVisits);

  const openGoogleMapsDirections = async (student_id, teacher_id) => {
    const ua = navigator.userAgent || '';
    const isAndroid = /Android/i.test(ua);
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isMobile = isAndroid || isIOS;
    const popup = !isMobile
      ? window.open('', '_blank', 'noopener,noreferrer')
      : null;

    const navigate = (url) => {
      if (popup && !popup.closed) {
        popup.location.href = url;
      } else {
        window.location.href = url;
      }
    };

    try {
      const res = await API.get('/visit-map-locations', {
        params: { student_id, teacher_id },
      });
      const s = res.data?.student || {};
      const destLat = s.lat ?? s.latitude ?? s.primary_lat;
      const destLon = s.lon ?? s.longitude ?? s.primary_lon;

      if (destLat == null || destLon == null) {
        alert('Student location is missing.');
        if (popup && !popup.closed) popup.close();
        return;
      }

      const origin =
        location && location.lat && location.lon
          ? `${location.lat},${location.lon}`
          : '';
      const dest = `${destLat},${destLon}`;

      const webUrl = origin
        ? `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(
            origin
          )}&destination=${encodeURIComponent(
            dest
          )}&travelmode=driving`
        : `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(
            dest
          )}&travelmode=driving`;

      if (isAndroid) {
        const navUrl = `google.navigation:q=${encodeURIComponent(dest)}&mode=d`;
        const intentUrl = origin
          ? `intent://maps.google.com/maps?daddr=${encodeURIComponent(
              dest
            )}&saddr=${encodeURIComponent(
              origin
            )}&directionsmode=driving#Intent;scheme=https;package=com.google.android.apps.maps;end`
          : `intent://maps.google.com/maps?daddr=${encodeURIComponent(
              dest
            )}&directionsmode=driving#Intent;scheme=https;package=com.google.android.apps.maps;end`;
        navigate(navUrl);
        setTimeout(() => navigate(intentUrl), 250);
        setTimeout(() => navigate(webUrl), 900);
        return;
      }

      if (isIOS) {
        const iosAppUrl = origin
          ? `comgooglemaps://?saddr=${encodeURIComponent(
              origin
            )}&daddr=${encodeURIComponent(
              dest
            )}&directionsmode=driving`
          : `comgooglemaps://?daddr=${encodeURIComponent(
              dest
            )}&directionsmode=driving`;

        const appleUrl = origin
          ? `maps://?saddr=${encodeURIComponent(
              origin
            )}&daddr=${encodeURIComponent(
              dest
            )}&dirflg=d`
          : `maps://?daddr=${encodeURIComponent(dest)}&dirflg=d`;

        navigate(iosAppUrl);
        setTimeout(() => navigate(appleUrl), 600);
        setTimeout(() => navigate(webUrl), 1300);
        return;
      }

      navigate(webUrl);
    } catch (e) {
      console.error(e);
      alert('Failed to open directions.');
      if (popup && !popup.closed) popup.close();
    }
  };

  // RENDER

  return (
    <div className="w-full px-0 sm:px-1">
      <h2 className="text-lg font-bold mb-4">My Visits</h2>

      {/* FILTERS */}
      <div className="w-full bg-white rounded-lg shadow-sm p-3 sm:p-4 mb-4 flex flex-col gap-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="flex flex-col">
            <label className="text-xs mb-1">Date From</label>
            <input
              type="date"
              value={filters.dateFrom}
              onChange={e =>
                setFilters(f => ({ ...f, dateFrom: e.target.value }))
              }
              className="border p-2 rounded"
            />
          </div>
          <div className="flex flex-col">
            <label className="text-xs mb-1">Date To</label>
            <input
              type="date"
              value={filters.dateTo}
              onChange={e =>
                setFilters(f => ({ ...f, dateTo: e.target.value }))
              }
              className="border p-2 rounded"
            />
          </div>
        </div>
        <div className="flex gap-2 mt-1 flex-wrap">
          <button
            onClick={() => setQuickFilter('today')}
            className="text-xs bg-gray-200 rounded px-2 py-1"
          >
            Today
          </button>
          <button
            onClick={() => setQuickFilter('week')}
            className="text-xs bg-gray-200 rounded px-2 py-1"
          >
            This Week
          </button>
          <button
            onClick={() => setQuickFilter('future')}
            className="text-xs bg-gray-200 rounded px-2 py-1"
          >
            All Future
          </button>
        </div>
        {studentOptions.length > 0 && (
          <div className="flex flex-col mt-2 max-w-xs">
            <label className="text-xs mb-1">Filter by Student</label>
            <select
              className="border p-2 rounded"
              value={studentFilter}
              onChange={e => setStudentFilter(e.target.value)}
            >
              <option value="">-- All students --</option>
              {studentOptions.map(s => (
                <option key={s.id} value={s.id}>
                  {(s.last_name || '') +
                    (s.last_name ? ', ' : '') +
                    (s.first_name || '')}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {!serverDateStr ? (
        <div className="p-4 text-gray-500 text-center">
          Loading server time...
        </div>
      ) : loading ? (
        <div className="p-6 text-gray-400 text-center">Loading...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div className="text-gray-500 mt-6">
          No visits found for the selected criteria.
        </div>
      ) : (
        Object.entries(grouped).map(([date, group]) => (
          <div
            key={date}
            className="mb-6 shadow rounded-lg bg-white border"
          >
            <div className="font-bold text-base px-4 py-3 bg-gray-50 rounded-t-lg flex items-center">
              <span>{date}</span>
              <span className="ml-3 bg-blue-200 text-blue-900 text-xs px-2 py-0.5 rounded-full">
                {group.length} visits
              </span>
            </div>

            {/* DESKTOP */}
            <div className="overflow-x-auto hidden sm:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="px-2 py-1">Student</th>
                    <th className="px-2 py-1">Time</th>
                    <th className="px-2 py-1 w-40">Status</th>
                    <th className="px-2 py-1">Check-in</th>
                    <th className="px-2 py-1">Checkout</th>
                    <th className="px-2 py-1">G-sign</th>
                    <th className="px-2 py-1"></th>
                  </tr>
                </thead>
                <tbody>
                  {group.map(v => {
                    const {
                      rowClass,
                      hideActions,
                      reassigned,
                      status,
                      visitDateStr,
                      isCanceled,
                    } = getVisitRowProps(v, serverDateStr);

                    const hasCheckin = !!v.checkin_time;
                    const hasCheckout = !!v.checkout_time;
                    const canCancelVisit = canCancel(v, visitDateStr); // *** CHANGED

                    let checkinContent = null;
                    let checkoutContent = null;
                    let gsignContent = null;
                    let editBtnsContent = null;

                    if (isCanceled) {
                      const exp = (
                        <span className="italic text-xs text-gray-400">
                          Expired
                        </span>
                      );
                      checkinContent = exp;
                      checkoutContent = exp;
                      gsignContent = exp;
                    } else if (
                      visitDateStr < serverDateStr &&
                      v.status === 'draft'
                    ) {
                      const exp = (
                        <span className="italic text-xs text-gray-400">
                          Expired
                        </span>
                      );
                      checkinContent = exp;
                      checkoutContent = exp;
                      gsignContent = exp;
                    } else if (visitDateStr > serverDateStr) {
                      const wait = (
                        <span className="italic text-xs text-yellow-500">
                          Waiting
                        </span>
                      );
                      checkinContent = wait;
                      checkoutContent = wait;
                      gsignContent = wait;
                    }

                    // check-in
                    if (!checkinContent) {
                      if (hasCheckin) {
                        checkinContent =
                          v.checkin_time_local || (
                            <span className="text-xs text-green-700">
                              Checked in
                            </span>
                          );
                      } else if (!hideActions) {
                        checkinContent = (
                          <button
                            onClick={() => handleCheckin(v.id)}
                            className="bg-blue-500 text-white px-2 py-1 rounded text-xs"
                          >
                            Check-in
                          </button>
                        );
                      }
                    }

                    // checkout
                    if (!checkoutContent) {
                      if (hasCheckout) {
                        checkoutContent =
                          v.checkout_time_local || (
                            <span className="text-xs text-green-700">
                              Checked out
                            </span>
                          );
                      } else if (!hideActions && hasCheckin) {
                        checkoutContent = (
                          <button
                            onClick={() => handleCheckout(v)}
                            className="bg-green-500 text-white px-2 py-1 rounded text-xs"
                          >
                            Checkout
                          </button>
                        );
                      }
                    }

                    // g-sign
                    if (!gsignContent && !hideActions) {
                      gsignContent = v.g_sign_confirmed ? '✅' : '❌';
                    }

                    // akcije
                    // *** CHANGED: prikazujemo container i ako je hideActions=true,
                    //              ali samo ako postoji canCancelVisit.
                    if (!hideActions || canCancelVisit) {
                      // *** CHANGED: QR samo za TEKUĆI dan, draft, ne-canceled
                      const canShowQR =
                        !v.g_sign_confirmed &&
                        visitDateStr === serverDateStr &&
                        !isCanceled &&
                        v.status === 'draft';

                      // *** CHANGED: Edit Notes samo kada su ostale akcije omogućene
                      const canEditNotes =
                        !hideActions && (hasCheckin || hasCheckout);

                      editBtnsContent = (
                        <div className="flex flex-col gap-1">
                          {canEditNotes && (
                            <button
                              onClick={() =>
                                openNotesModal(v, 'edit')
                              }
                              className="bg-yellow-500 text-white px-2 py-1 rounded text-xs"
                            >
                              Edit Notes
                            </button>
                          )}
                          {canShowQR && (
                            <button
                              onClick={() => {
                                setQrModalVisitId(v.id);
                                setVisitConfirmed(false);
                              }}
                              className="bg-gray-500 text-white px-2 py-1 rounded text-xs"
                            >
                              Show QR
                            </button>
                          )}
                          {canCancelVisit && (
                            <button
                              onClick={() =>
                                setCancelVisitId(v.id)
                              }
                              className="bg-purple-700 text-white px-2 py-1 rounded text-xs"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      );
                    }

                    return (
                      <tr key={v.id} className={rowClass}>
                        <td className="px-2 py-1 flex items-center gap-2">
                          <span
                            onClick={() =>
                              openStudentModal(v.student_id)
                            }
                            className="text-blue-700 underline cursor-pointer hover:text-blue-900"
                          >
                            {(v.student_last_name || '') +
                              (v.student_last_name ? ', ' : '') +
                              (v.student_first_name || '')}
                          </span>
                          <ProgramBadge program_type={v.program_type} />
                          <button
                            onClick={() =>
                              openMapModal(v.student_id, v.teacher_id)
                            }
                            className="ml-1 text-gray-400 hover:text-blue-600"
                            title="Show on Map"
                          >
                            <MapPin size={16} />
                          </button>
                          <button
                            onClick={() =>
                              openGoogleMapsDirections(
                                v.student_id,
                                v.teacher_id
                              )
                            }
                            className="ml-1 text-green-600 hover:text-green-800"
                            title="Open Google Maps directions"
                          >
                            <SiGooglemaps size={16} />
                          </button>
                          <button
                            onClick={() =>
                              openNotesHistoryModal(
                                v.student_id,
                                `${v.student_last_name || ''}${
                                  v.student_last_name ? ', ' : ''
                                }${v.student_first_name || ''}`
                              )
                            }
                            className="ml-1 text-purple-700 hover:text-purple-900"
                            title="Show Notes History"
                          >
                            <FaBookOpen size={16} />
                          </button>
                        </td>
                        <td className="px-2 py-1">
                          <VisitTimeType
                            scheduled_time={v.scheduled_time}
                            type={v.type}
                            event_end_time={v.event_end_time}
                          />
                        </td>
                        <td className="px-2 py-1 flex flex-col">
                          <StatusBadge status={v.status}>
                            {v.status === 'canceled'
                              ? 'Canceled'
                              : v.status}
                          </StatusBadge>
                          {isCanceled && v.notes && (
                            <span className="mt-1 text-xs text-purple-700 italic">
                              {v.notes}
                            </span>
                          )}
                          {reassigned &&
                            status &&
                            !isCanceled && (
                              <span
                                className={`mt-1 text-xs ${
                                  status === 'rejected'
                                    ? 'text-red-700'
                                    : 'text-orange-700'
                                }`}
                              >
                                Substitute: {status}
                              </span>
                            )}
                        </td>
                        <td className="px-2 py-1">{checkinContent}</td>
                        <td className="px-2 py-1">{checkoutContent}</td>
                        <td className="px-2 py-1">{gsignContent}</td>
                        <td className="px-2 py-1">{editBtnsContent}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* MOBILE */}
            <div className="sm:hidden flex flex-col gap-2 p-2">
              {group.map(v => {
                const {
                  rowClass,
                  hideActions,
                  reassigned,
                  status,
                  visitDateStr,
                  isCanceled,
                } = getVisitRowProps(v, serverDateStr);

                const hasCheckin = !!v.checkin_time;
                const hasCheckout = !!v.checkout_time;
                const canCancelVisit = canCancel(v, visitDateStr); // *** CHANGED

                let checkinContent = null;
                let checkoutContent = null;
                let gsignContent = null;
                let editBtnsContent = null;

                if (isCanceled) {
                  const exp = (
                    <span className="italic text-xs text-gray-400">
                      Expired
                    </span>
                  );
                  checkinContent = exp;
                  checkoutContent = exp;
                  gsignContent = exp;
                } else if (
                  visitDateStr < serverDateStr &&
                  v.status === 'draft'
                ) {
                  const exp = (
                    <span className="italic text-xs text-gray-400">
                      Expired
                    </span>
                  );
                  checkinContent = exp;
                  checkoutContent = exp;
                  gsignContent = exp;
                } else if (visitDateStr > serverDateStr) {
                  const wait = (
                    <span className="italic text-xs text-yellow-500">
                      Waiting
                    </span>
                  );
                  checkinContent = wait;
                  checkoutContent = wait;
                  gsignContent = wait;
                }

                if (!checkinContent) {
                  if (hasCheckin) {
                    checkinContent =
                      v.checkin_time_local || (
                        <span className="text-xs text-green-700">
                          Checked in
                        </span>
                      );
                  } else if (!hideActions) {
                    checkinContent = (
                      <button
                        onClick={() => handleCheckin(v.id)}
                        className="bg-blue-500 text-white px-2 py-1 rounded text-xs w-full"
                      >
                        Check-in
                      </button>
                    );
                  }
                }

                if (!checkoutContent) {
                  if (hasCheckout) {
                    checkoutContent =
                      v.checkout_time_local || (
                        <span className="text-xs text-green-700">
                          Checked out
                        </span>
                      );
                  } else if (!hideActions && hasCheckin) {
                    checkoutContent = (
                      <button
                        onClick={() => handleCheckout(v)}
                        className="bg-green-500 text-white px-2 py-1 rounded text-xs w-full"
                      >
                        Checkout
                      </button>
                    );
                  }
                }

                if (!gsignContent && !hideActions) {
                  gsignContent = v.g_sign_confirmed ? '✅' : '❌';
                }

                if (!hideActions || canCancelVisit) {
                  const canShowQR =
                    !v.g_sign_confirmed &&
                    visitDateStr === serverDateStr &&
                    !isCanceled &&
                    v.status === 'draft';

                  const canEditNotes =
                    !hideActions && (hasCheckin || hasCheckout);

                  editBtnsContent = (
                    <div className="flex flex-col gap-1">
                      {canEditNotes && (
                        <button
                          onClick={() =>
                            openNotesModal(v, 'edit')
                          }
                          className="bg-yellow-500 text-white px-2 py-1 rounded text-xs w-full"
                        >
                          Edit Notes
                        </button>
                      )}
                      {canShowQR && (
                        <button
                          onClick={() => {
                            setQrModalVisitId(v.id);
                            setVisitConfirmed(false);
                          }}
                          className="bg-gray-500 text-white px-2 py-1 rounded text-xs w-full"
                        >
                          Show QR
                        </button>
                      )}
                      {canCancelVisit && (
                        <button
                          onClick={() =>
                            setCancelVisitId(v.id)
                          }
                          className="bg-purple-700 text-white px-2 py-1 rounded text-xs w-full"
                        >
                          Cancel
                        </button>
                      )}
                    </div>
                  );
                }

                return (
                  <div
                    key={v.id}
                    className={`rounded-2xl border shadow p-3 mb-1 ${rowClass}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1 flex items-center gap-1 flex-wrap">
                        <span
                          onClick={() =>
                            openStudentModal(v.student_id)
                          }
                          className="text-blue-700 underline cursor-pointer hover:text-blue-900"
                        >
                          {(v.student_last_name || '') +
                            (v.student_last_name ? ', ' : '') +
                            (v.student_first_name || '')}
                        </span>
                        <ProgramBadge program_type={v.program_type} />
                        <button
                          onClick={() =>
                            openMapModal(
                              v.student_id,
                              v.teacher_id
                            )
                          }
                          className="ml-1 text-gray-400 hover:text-blue-600"
                          title="Show on Map"
                        >
                          <MapPin size={16} />
                        </button>
                        <button
                          onClick={() =>
                            openGoogleMapsDirections(
                              v.student_id,
                              v.teacher_id
                            )
                          }
                          className="ml-1 text-green-600 hover:text-green-800"
                          title="Open Google Maps directions"
                        >
                          <SiGooglemaps size={16} />
                        </button>
                        <button
                          onClick={() =>
                            openNotesHistoryModal(
                              v.student_id,
                              `${v.student_last_name || ''}${
                                v.student_last_name ? ', ' : ''
                              }${v.student_first_name || ''}`
                            )
                          }
                          className="ml-1 text-purple-700 hover:text-purple-900"
                          title="Show Notes History"
                        >
                          <FaBookOpen size={16} />
                        </button>
                      </div>
                      <div className="flex-1 text-right">
                        <VisitTimeType
                          scheduled_time={v.scheduled_time}
                          type={v.type}
                          event_end_time={v.event_end_time}
                        />
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 items-center mt-2">
                      <StatusBadge status={v.status}>
                        {v.status === 'canceled'
                          ? 'Canceled'
                          : v.status}
                      </StatusBadge>
                      {v.status === 'canceled' && v.notes && (
                        <span className="text-xs text-purple-700 italic">
                          {v.notes}
                        </span>
                      )}
                      {reassigned &&
                        status &&
                        !isCanceled && (
                          <span
                            className={`text-xs ${
                              status === 'rejected'
                                ? 'text-red-700'
                                : 'text-orange-700'
                            }`}
                          >
                            Substitute: {status}
                          </span>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-2 mt-3">
                      <div>
                        <span className="block text-xs text-gray-600">
                          Check-in
                        </span>
                        {checkinContent}
                      </div>
                      <div>
                        <span className="block text-xs text-gray-600">
                          Checkout
                        </span>
                        {checkoutContent}
                      </div>
                      <div>
                        <span className="block text-xs text-gray-600">
                          G-sign
                        </span>
                        {gsignContent}
                      </div>
                      <div className="flex flex-col gap-1 mt-2">
                        {editBtnsContent}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))
      )}

      {/* Notes modal */}
      {showNotesModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md max-h-[80vh] overflow-y-auto">
            <h3 className="text-lg font-bold mb-4">
              {notesEditMode
                ? 'Edit Visit Notes'
                : 'Enter Visit Notes (for all domains)'}
            </h3>
            {DOMAINS.map(d => (
              <div key={d.key} className="mb-3">
                <label className="block text-sm font-medium mb-1">
                  {d.label}
                </label>
                <textarea
                  value={notesFields[d.key] || ''}
                  onChange={e =>
                    setNotesFields(f => ({
                      ...f,
                      [d.key]: e.target.value,
                    }))
                  }
                  className="w-full border px-3 py-2 rounded"
                  rows={4}
                />
              </div>
            ))}
            <div className="flex justify-end gap-2 mt-2">
              <button
                onClick={() => setShowNotesModal(false)}
                className="px-4 py-2 bg-gray-300 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNotes}
                disabled={notesSaving}
                className={`px-4 py-2 bg-blue-600 text-white rounded ${
                  notesSaving ? 'opacity-50 pointer-events-none' : ''
                }`}
              >
                {notesSaving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR modal */}
      {qrModalVisitId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded shadow-lg w-full max-w-md text-center">
            <h2 className="text-lg font-semibold mb-4">
              Parent Signature – G-sign Confirmation
            </h2>
            {visitConfirmed ? (
              <div className="text-green-600 text-xl font-bold">
                ✅ Visit Confirmed
              </div>
            ) : (
              <>
                <p className="mb-4">
                  Scan this QR code with the parent's device:
                </p>
                <div className="inline-block bg-white p-4 shadow rounded mx-auto">
                  <QRCode
                    value={`${window.location.origin}/visit/${qrModalVisitId}/confirm`}
                    size={192}
                  />
                </div>
                <p className="mt-4 text-sm text-gray-600 break-all">
                  {`${window.location.origin}/visit/${qrModalVisitId}/confirm`}
                </p>
              </>
            )}
            <button
              onClick={handleCloseQRModal}
              className="mt-6 bg-gray-600 text-white px-4 py-2 rounded"
            >
              Close
            </button>
          </div>
        </div>
      )}

      <CancelModal
        open={!!cancelVisitId}
        onClose={() => setCancelVisitId(null)}
        onSubmit={handleCancelVisit}
        loading={cancelLoading}
      />

      <StudentDetailsModal
        open={studentDetailsModal.open}
        onClose={() =>
          setStudentDetailsModal({ open: false, student: null })
        }
        student={studentDetailsModal.student}
      />

      {showNotesHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-5 rounded shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-lg font-bold">
                Notes History: {notesHistoryStudent}
              </h3>
              <button
                className="text-gray-500"
                onClick={() => setShowNotesHistory(false)}
              >
                ✕
              </button>
            </div>
            {notesHistoryLoading ? (
              <div className="p-4 text-center text-gray-400">
                Loading...
              </div>
            ) : notesHistory.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No notes found for this student.
              </div>
            ) : (
              <div className="space-y-4">
                {notesHistory.map((entry, idx) => (
                  <div
                    key={idx}
                    className="border rounded p-3 bg-gray-50"
                  >
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-semibold text-blue-900">
                        {entry.teacher_name}
                      </span>
                      <span className="text-xs text-gray-500">
                        {entry.scheduled_at
                          .slice(0, 16)
                          .replace('T', ' ')}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                      {DOMAINS.map(dom =>
                        entry[dom.key] &&
                        entry[dom.key].trim() !== '' ? (
                          <div key={dom.key}>
                            <span className="font-semibold">
                              {dom.label}:
                            </span>{' '}
                            <span>{entry[dom.key]}</span>
                          </div>
                        ) : null
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <MapModal
        open={mapModal.open}
        onClose={() =>
          setMapModal({ open: false, student: null, teacher: null })
        }
        student={mapModal.student}
        teacher={mapModal.teacher}
      />
    </div>
  );
}

export default MyVisits;






